import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Camera, RefreshCw, ScanFace, X, ImageUp, AlertTriangle } from 'lucide-react';

const SCAN_STEPS = [
  'Detecting facial landmarks…',
  'Analyzing skin texture & pores…',
  'Measuring redness & inflammation…',
  'Assessing hydration & oiliness…',
  'Scanning for spots & dark circles…',
  'Estimating skin age & overall health…',
  'Compiling your clinical report…',
];

const FaceScanModal = ({ open, onOpenChange, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [phase, setPhase] = useState('camera'); // camera | scanning
  const [captured, setCaptured] = useState(null); // data URL preview
  const [error, setError] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setError('camera');
    }
  }, []);

  // Manage camera lifecycle with modal open + phase
  useEffect(() => {
    if (open && phase === 'camera') {
      startCamera();
    }
    return () => stopStream();
  }, [open, phase, startCamera, stopStream]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      stopStream();
      setPhase('camera');
      setCaptured(null);
      setError(null);
      setStepIdx(0);
    }
  }, [open, stopStream]);

  // Cycle scan step text while analyzing
  useEffect(() => {
    if (phase !== 'scanning') return;
    const id = setInterval(() => setStepIdx((i) => (i + 1) % SCAN_STEPS.length), 1100);
    return () => clearInterval(id);
  }, [phase]);

  const runAnalysis = useCallback(async (dataUrl) => {
    setCaptured(dataUrl);
    setPhase('scanning');
    setStepIdx(0);
    stopStream();
    try {
      await onCapture(dataUrl.split(',')[1]);
      onOpenChange(false);
    } catch (e) {
      setError('analysis');
      setPhase('camera');
    }
  }, [onCapture, onOpenChange, stopStream]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = 720;
    canvas.height = 960;
    const ctx = canvas.getContext('2d');
    // center-crop to 3:4
    const targetRatio = 3 / 4;
    let sw = video.videoWidth;
    let sh = sw / targetRatio;
    if (sh > video.videoHeight) { sh = video.videoHeight; sw = sh * targetRatio; }
    const sx = (video.videoWidth - sw) / 2;
    const sy = (video.videoHeight - sh) / 2;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    runAnalysis(canvas.toDataURL('image/jpeg', 0.9));
  }, [runAnalysis]);

  const handleFilePick = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => runAnalysis(reader.result);
    reader.readAsDataURL(file);
  }, [runAnalysis]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-0 bg-[#161b17] text-white" data-testid="face-scan-modal">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ScanFace className="w-5 h-5 text-[#4ADE80]" />
              <h3 className="font-heading text-lg font-bold">AI Skin Analysis</h3>
            </div>
            <button onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white" data-testid="face-scan-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="face-scan-stage">
            {phase === 'camera' && !error && (
              <>
                <video ref={videoRef} playsInline muted className="face-scan-mirror" />
                <div className="face-guide-oval" />
                <div className="absolute bottom-3 left-0 right-0 text-center px-4">
                  <p className="text-xs text-white/80 bg-black/40 inline-block px-3 py-1 rounded-full">
                    Center your face · good lighting · no glasses
                  </p>
                </div>
              </>
            )}

            {phase === 'camera' && error === 'camera' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <AlertTriangle className="w-10 h-10 text-[#E2C56B] mb-3" />
                <p className="text-sm font-semibold">Camera unavailable</p>
                <p className="text-xs text-white/60 mt-1 mb-4">Allow camera access, or upload a photo instead.</p>
                <Button onClick={() => fileInputRef.current?.click()} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full" data-testid="face-scan-fallback-upload">
                  <ImageUp className="w-4 h-4 mr-2" /> Upload a photo
                </Button>
              </div>
            )}

            {phase === 'scanning' && (
              <>
                <img src={captured} alt="Your scan" />
                <div className="face-scan-dim" />
                <div className="face-scan-grid" />
                <div className="face-scan-line" />
                <div className="absolute inset-x-0 bottom-4 text-center px-4">
                  <p className="text-sm font-semibold text-white" data-testid="face-scan-step">{SCAN_STEPS[stepIdx]}</p>
                  <div className="mt-2 h-1 w-2/3 mx-auto bg-white/15 rounded-full overflow-hidden">
                    <div className="h-full bg-[#4ADE80] rounded-full" style={{ width: `${((stepIdx + 1) / SCAN_STEPS.length) * 100}%`, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              </>
            )}
          </div>

          {error === 'analysis' && (
            <p className="text-xs text-[#E2A593] mt-3 text-center">Analysis failed. Please try again.</p>
          )}

          {phase === 'camera' && !error && (
            <div className="mt-5 flex items-center gap-3">
              <Button onClick={handleCapture} className="flex-1 bg-[#4ADE80] hover:bg-[#94a37b] text-[#161b17] font-bold rounded-full h-12" data-testid="face-scan-capture-btn">
                <Camera className="w-5 h-5 mr-2" /> Scan My Skin
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-12" data-testid="face-scan-upload-btn">
                <ImageUp className="w-5 h-5" />
              </Button>
            </div>
          )}

          {phase === 'scanning' && (
            <div className="mt-5 flex items-center justify-center gap-2 text-white/70 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> Analyzing your skin… this takes a few seconds
            </div>
          )}

          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFilePick} className="hidden" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FaceScanModal;
